import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { MessageSquare, Send, RotateCcw, Save, Megaphone, ArrowRight } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useSmsAccount,
  useSmsTemplates,
  useSaveSmsTemplate,
  useResetSmsTemplate,
  useSmsMessages,
  useSendSms,
  estimateSegments,
  type SmsTemplate,
  type TemplateKind,
} from '@/lib/sms';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/sms')({
  staticData: { crumb: 'SMS' },
  component: SmsPage,
});

const KIND_LABELS: Record<TemplateKind, string> = {
  booking_confirmation: 'Potwierdzenie wizyty',
  booking_reminder_t24h: 'Przypomnienie T-24h',
  booking_reminder_t2h: 'Przypomnienie T-2h',
  booking_cancellation: 'Odwołanie wizyty',
  recall: 'Recall (powtórna wizyta)',
  custom: 'Niestandardowy',
};

function SmsPage() {
  const account = useSmsAccount();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">SMS</h1>
        <p className="mt-1 text-sm text-text-muted">
          Wysyłka SMS przez smsapi.pl — potwierdzenia, przypomnienia, recall. Pacjenci mogą się
          wypisać odpowiadając STOP w dowolnym momencie.
        </p>
      </header>

      <AccountCard data={account.data} pending={account.isPending} />
      <Link
        to={'/sms/campaigns' as never}
        className="group flex items-center justify-between gap-3 rounded-[var(--radius-card)] bg-bg p-5 ring-1 ring-black/5 hover:ring-black/10"
      >
        <div className="flex items-center gap-3">
          <div className="grid size-10 place-items-center rounded-md bg-accent/10 text-accent ring-1 ring-accent/20">
            <Megaphone className="size-5" aria-hidden="true" />
          </div>
          <div>
            <p className="font-display text-base font-semibold text-text">Kampanie SMS</p>
            <p className="mt-0.5 text-sm text-text-muted">
              Sekwencje SMS-ów wokół wizyt — potwierdzenia, przypomnienia, recall.
            </p>
          </div>
        </div>
        <ArrowRight
          className="size-4 text-text-subtle transition group-hover:translate-x-0.5 group-hover:text-text"
          aria-hidden="true"
        />
      </Link>
      <TemplatesCard />
      <ManualSendCard />
      <MessagesCard />
    </div>
  );
}

function AccountCard({
  data,
  pending,
}: {
  data: ReturnType<typeof useSmsAccount>['data'];
  pending: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="size-5" aria-hidden="true" /> Konto smsapi.pl
        </CardTitle>
        <CardDescription>
          Aby wysyłać SMS, operator wprowadza token z panelu smsapi.pl oraz zweryfikowany
          nadawcę (sender id).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {pending ? (
          <p className="text-text-muted">Ładowanie…</p>
        ) : !data?.configured ? (
          <p className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
            smsapi.pl nie jest skonfigurowane. Skontaktuj się z operatorem, aby aktywować SMS.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            <Metric label="Nadawca" value={data.sender_id ?? '—'} mono />
            <Metric label="Konto" value={data.username ?? '—'} mono />
            <Metric label="Pozostałe punkty" value={data.points_remaining ?? '—'} mono />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TemplatesCard() {
  const { data: templates } = useSmsTemplates();
  const save = useSaveSmsTemplate();
  const reset = useResetSmsTemplate();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Szablony</CardTitle>
        <CardDescription>
          Treść szablonów z menu poniżej. Zmiana wpływa na automatyczne wysyłki (przypomnienia
          o wizycie, recall). Marketing kończy się dopiskiem „STOP=koniec" wymaganym przez RODO.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {(templates ?? []).map((t) => (
          <TemplateEditor
            key={t.kind}
            template={t}
            onSave={(body, name, enabled) =>
              save.mutate({ kind: t.kind, body, name, enabled })
            }
            onReset={() => reset.mutate(t.kind)}
            busy={save.isPending || reset.isPending}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function TemplateEditor({
  template,
  onSave,
  onReset,
  busy,
}: {
  template: SmsTemplate;
  onSave: (body: string, name: string, enabled: boolean) => void;
  onReset: () => void;
  busy: boolean;
}) {
  const [body, setBody] = React.useState(template.body);
  const [enabled, setEnabled] = React.useState(template.enabled);
  React.useEffect(() => {
    setBody(template.body);
    setEnabled(template.enabled);
  }, [template.body, template.enabled, template.customized]);
  const dirty = body !== template.body || enabled !== template.enabled;
  const segs = estimateSegments(body);
  const stopRequired = template.is_marketing && !/STOP/i.test(body);

  return (
    <div className="rounded-[var(--radius-card)] bg-bg p-5 ring-1 ring-black/5">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <p className="font-display text-sm font-semibold text-text">
            {KIND_LABELS[template.kind]}
            {template.is_marketing ? (
              <span className="ml-2 rounded-full bg-warning/10 px-2 py-0.5 text-xs text-warning ring-1 ring-warning/20">
                marketing
              </span>
            ) : (
              <span className="ml-2 rounded-full bg-success/10 px-2 py-0.5 text-xs text-success ring-1 ring-success/20">
                transakcyjny
              </span>
            )}
          </p>
          <p className="mt-1 text-xs text-text-muted">
            Zmienne:{' '}
            {template.variables.map((v) => (
              <code key={v} className="mr-1 rounded bg-surface px-1 py-0.5 text-[10px]">
                {`{{${v}}}`}
              </code>
            ))}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-text">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="size-4 rounded border-border text-accent focus:ring-accent"
          />
          Aktywny
        </label>
      </div>
      <Field label="Treść" htmlFor={`tpl-${template.kind}`} className="mt-3">
        <Textarea
          id={`tpl-${template.kind}`}
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3 text-xs">
        <span
          className={cn(
            'text-text-subtle',
            segs.segments > 2 && 'text-warning',
            segs.segments > 3 && 'text-error'
          )}
        >
          {segs.chars} zn. · {segs.segments} {segs.segments === 1 ? 'segment' : 'segmenty'} ·{' '}
          {segs.encoding === 'gsm-7' ? 'GSM-7' : 'Unicode'}
        </span>
        {stopRequired ? (
          <span className="text-warning">Dodaj „STOP=koniec" dla zgodności RODO</span>
        ) : null}
        <div className="flex items-center gap-2">
          {template.customized ? (
            <Button type="button" variant="ghost" size="sm" onClick={onReset} disabled={busy}>
              <RotateCcw className="size-3.5" aria-hidden="true" /> Przywróć domyślny
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={!dirty || busy}
            onClick={() => onSave(body, KIND_LABELS[template.kind], enabled)}
          >
            <Save className="size-3.5" aria-hidden="true" />
            Zapisz
          </Button>
        </div>
      </div>
    </div>
  );
}

function ManualSendCard() {
  const send = useSendSms();
  const [to, setTo] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const [test, setTest] = React.useState(true);
  const [feedback, setFeedback] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const segs = estimateSegments(msg);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Wyślij ad-hoc SMS</CardTitle>
        <CardDescription>
          Pojedyncza wysyłka do dowolnego numeru (operator/recepcja). Domyślnie w trybie test —
          smsapi sprawdzi parametry + zwróci koszt, bez faktycznej wysyłki.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Telefon" htmlFor="sms-to" hint="np. 501 234 567">
            <Input id="sms-to" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Tryb" htmlFor="sms-test">
            <label className="flex h-10 items-center gap-2 text-sm">
              <input
                id="sms-test"
                type="checkbox"
                checked={test}
                onChange={(e) => setTest(e.target.checked)}
                className="size-4 rounded border-border text-accent focus:ring-accent"
              />
              Test (bez wysyłki)
            </label>
          </Field>
        </div>
        <Field label="Treść" htmlFor="sms-msg" hint={`${segs.chars} zn. · ${segs.segments} seg.`}>
          <Textarea
            id="sms-msg"
            rows={3}
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
          />
        </Field>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            disabled={!to || msg.length < 5 || send.isPending}
            onClick={async () => {
              setFeedback(null);
              try {
                const r = (await send.mutateAsync({ to, message: msg, test })) as {
                  ok: boolean;
                  reason?: string;
                  detail?: string;
                };
                if (r.ok) {
                  setFeedback({
                    kind: 'ok',
                    text: test ? 'Test OK — koszt oszacowany, SMS nie wysłany.' : 'SMS wysłany.',
                  });
                  setMsg('');
                } else {
                  setFeedback({ kind: 'err', text: r.reason ?? 'Nieznany błąd.' });
                }
              } catch (e) {
                setFeedback({ kind: 'err', text: (e as Error).message });
              }
            }}
          >
            <Send className="size-4" aria-hidden="true" />
            {send.isPending ? 'Wysyłanie…' : test ? 'Test wysyłki' : 'Wyślij SMS'}
          </Button>
        </div>
        {feedback ? (
          <p
            className={
              feedback.kind === 'ok'
                ? 'rounded-md bg-success/10 p-3 text-sm text-success ring-1 ring-success/20'
                : 'rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20'
            }
            role="status"
          >
            {feedback.text}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MessagesCard() {
  const { data: messages, isPending } = useSmsMessages();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ostatnie wiadomości</CardTitle>
        <CardDescription>200 ostatnich SMS-ów. Statusy aktualizujemy z callbacków smsapi.</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isPending ? (
          <p className="p-6 text-sm text-text-muted">Ładowanie…</p>
        ) : (messages ?? []).length === 0 ? (
          <p className="p-6 text-sm text-text-muted">Brak wysłanych wiadomości.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[48rem] text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wide text-text-subtle">
                <tr>
                  <th className="px-4 py-3 font-medium">Czas</th>
                  <th className="px-4 py-3 font-medium">Telefon</th>
                  <th className="px-4 py-3 font-medium">Treść</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Segm.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-bg">
                {messages!.map((m) => (
                  <tr key={m.id}>
                    <td className="px-4 py-3 text-xs text-text-muted whitespace-nowrap">
                      {new Date(m.created_at * 1000).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{m.to_phone_redacted ?? '—'}</td>
                    <td className="px-4 py-3 text-text">
                      <span className="line-clamp-2 max-w-xl">{m.body}</span>
                      {m.template_kind ? (
                        <span className="mt-0.5 block text-[10px] text-text-subtle">
                          {m.template_kind}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={m.status} detail={m.status_detail} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{m.parts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status, detail }: { status: string; detail: string | null }) {
  const styles: Record<string, string> = {
    pending: 'bg-surface text-text-muted ring-border',
    queued: 'bg-accent/10 text-accent ring-accent/20',
    delivered: 'bg-success/10 text-success ring-success/20',
    failed: 'bg-error/10 text-error ring-error/20',
    blocked: 'bg-warning/10 text-warning ring-warning/20',
  };
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs ring-1',
        styles[status] ?? styles.pending
      )}
      title={detail ?? undefined}
    >
      {status}
    </span>
  );
}

function Metric({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md bg-surface p-3 ring-1 ring-black/5">
      <p className="text-xs uppercase tracking-wide text-text-subtle">{label}</p>
      <p className={cn('mt-1 text-sm', mono ? 'font-mono' : '')}>{value}</p>
    </div>
  );
}
